import * as yup from "yup";
import React, { useState } from "react";

import { Box, Button, Grid, TextField } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import Title from "../../../common/Title";

import CallFilters from "./CallFilters";
import MessageFilters from "./MessageFilters";
import CuratedGroups from "./CuratedGroups";
import useAxios from "../../../../hooks/useAxios";
import useAlert from "../../../../hooks/useAlert";
import {
  __getTmobilejCallFilters,
  __getTmobilejMessageFilter,
  __getTmobilejSubscriber,
  __createTmobilejSubscriber,
} from "../../../../api/sim";

const lineValidationSchema = yup.object({
  number: yup
    .string("number")
    .min(10, "Should be exactly 10 digits long")
    .max(10, "Should be exactly 10 digits long")
    .required(),
});

const TmobilejFilters = () => {
  const { setAlertMessage } = useAlert();
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [subscriberId, setSubscriberId] = useState("");
  const [callFilterData, setCallFilterData] = useState(null);
  const [messageFilterData, setMessageFilterData] = useState(null);
  const [showCallFilters, setShowCallFilters] = useState(false);
  const [showMessageFilters, setShowMessageFilters] = useState(false);
  const [showCuratedGroups, setShowCuratedGroups] = useState(false);

  const [, getSubscriberApi, getSubscriberLoading] = useAxios();
  const [, createSubscriberApi, createSubscriberLoading] = useAxios();
  const [fetchCallResp, fetchCallApi, fetchCallLoading] = useAxios();
  const [fetchMsgResp, fetchMsgApi, fetchMsgLoading] = useAxios();

  const validatePhone = (val) => {
    if (!val) return "Phone number is required";
    const obtainedData = { number: String(val || "").trim() };
    try {
      lineValidationSchema.validateSync(obtainedData);
    } catch (e) {
      return "Phone number must be digits-only and 10 digits";
    }
    if (isNaN(obtainedData.number))
      return "Phone number must be digits-only and 10 digits";
    return "";
  };

  React.useEffect(() => {
    if (fetchCallResp?.status && fetchCallResp?.data) {
      setCallFilterData(fetchCallResp.data);
      setShowCallFilters(true);
      setShowMessageFilters(false); // Hide message filters when call filters are fetched
    }
  }, [fetchCallResp]);

  React.useEffect(() => {
    if (fetchMsgResp?.status && fetchMsgResp?.data) {
      setMessageFilterData(fetchMsgResp.data);
      setShowMessageFilters(true);
      setShowCallFilters(false); // Hide call filters when message filters are fetched
    }
  }, [fetchMsgResp]);

  const handleGetSubscriber = async () => {
    const err = validatePhone(phone);
    setPhoneError(err);
    if (err) {
      setAlertMessage({ type: "error", message: err });
      return;
    }

    // Attempt to get existing subscriber
    const resp = await getSubscriberApi(__getTmobilejSubscriber(phone));
    if (resp && resp.SubscriberId) {
      setSubscriberId(resp.SubscriberId);
      setAlertMessage({ type: "success", message: "Subscriber ID found" });
    } else {
      // If not found or empty, attempt to create subscriber
      const createResp = await createSubscriberApi(
        __createTmobilejSubscriber(phone),
      );
      if (createResp && createResp.SubscriberId) {
        setSubscriberId(createResp.SubscriberId);
        setAlertMessage({
          type: "success",
          message: "Subscriber created successfully",
        });
      } else {
        setAlertMessage({
          type: "error",
          message: "Failed to get or create subscriber",
        });
      }
    }
  };

  const handleToggleCuratedGroups = () => {
    setShowCuratedGroups(!showCuratedGroups);
    if (!showCuratedGroups) {
      setShowCallFilters(false);
      setShowMessageFilters(false);
    }
  };

  const handleFetchCallFilters = async () => {
    if (!subscriberId) return;
    setShowMessageFilters(false); // Hide message filters immediately when call filters button is clicked
    const resp = await fetchCallApi(__getTmobilejCallFilters(subscriberId));
    setShowCallFilters(true);
    if (!resp) {
      setCallFilterData(null);
    }
  };

  const handleFetchMessageFilters = async () => {
    if (!subscriberId) return;
    setShowCallFilters(false); // Hide call filters immediately when message filters button is clicked
    const resp = await fetchMsgApi(__getTmobilejMessageFilter(subscriberId));
    setShowMessageFilters(true);
    if (!resp) {
      setMessageFilterData(null);
    }
  };

  const isSubscriberLoading = getSubscriberLoading || createSubscriberLoading;

  return (
    <Box
      sx={{ width: "100%" }}
      display="flex"
      alignItems="center"
      flexDirection="column"
    >
      <Title
        variant="h5"
        content="T-Mobile J Filters"
        sx={{ mb: 3 }}
        gutterBottom
      />

      <Grid container spacing={2} direction="column" sx={{ maxWidth: 700 }}>
        <Grid item>
          <TextField
            fullWidth
            label="Phone Number (10 digits)"
            value={phone}
            error={Boolean(phoneError)}
            helperText={phoneError || "Digits only (e.g. 1234567890)"}
            onChange={(e) => {
              const v = e.target.value.trim();
              setPhone(v);
              setPhoneError(v ? validatePhone(v) : "");
              setSubscriberId(""); // Reset subscriberId on phone change
              setShowCallFilters(false);
              setShowMessageFilters(false);
              setShowCuratedGroups(false);
            }}
          />
        </Grid>

        <Grid
          item
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <LoadingButton
            loading={isSubscriberLoading}
            disabled={
              isSubscriberLoading ||
              Boolean(phoneError) ||
              !phone ||
              Boolean(subscriberId)
            }
            variant={subscriberId ? "contained" : "outlined"}
            color={subscriberId ? "success" : "primary"}
            onClick={handleGetSubscriber}
          >
            {subscriberId ? "Subscriber ID Verified" : "Get Subscriber ID"}
          </LoadingButton>

          {subscriberId && (
            <>
              <LoadingButton
                loading={fetchCallLoading}
                disabled={fetchCallLoading}
                variant={showCallFilters ? "contained" : "outlined"}
                onClick={handleFetchCallFilters}
              >
                Fetch Call Filters
              </LoadingButton>
              <LoadingButton
                loading={fetchMsgLoading}
                disabled={fetchMsgLoading}
                variant={showMessageFilters ? "contained" : "outlined"}
                onClick={handleFetchMessageFilters}
              >
                Fetch Message Filters
              </LoadingButton>
            </>
          )}

          <Button
            variant={showCuratedGroups ? "contained" : "outlined"}
            color="secondary"
            onClick={handleToggleCuratedGroups}
          >
            {showCuratedGroups
              ? "Hide Curated Groups"
              : "Manage Curated Groups"}
          </Button>
        </Grid>

        {showCuratedGroups && <CuratedGroups />}

        {showCallFilters && subscriberId && (
          <CallFilters
            phone={phone}
            subscriberId={subscriberId}
            phoneError={phoneError}
            validatePhone={validatePhone}
            initialData={callFilterData}
            onDataUpdate={setCallFilterData}
          />
        )}

        {showMessageFilters && subscriberId && (
          <MessageFilters
            phone={phone}
            subscriberId={subscriberId}
            phoneError={phoneError}
            validatePhone={validatePhone}
            initialData={messageFilterData}
            onDataUpdate={setMessageFilterData}
          />
        )}
      </Grid>
    </Box>
  );
};

export default TmobilejFilters;
